import { FC } from 'react';
import Typography from '@mui/material/Typography';
import SaveAlt from '@mui/icons-material/SaveAlt';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import ViewListOutlined from '@mui/icons-material/ViewListOutlined';
import { Control, useFieldArray } from 'react-hook-form';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import Switch from 'further-ui/components/Switch';
import Avatar from 'further-ui/components/Avatar';
import usePeriodEndReportPreviewPdf from './usePeriodEndReportPreviewPdf';
import useFinancialStatementsExcel from './useFinancialStatementsExcel';
import { PeriodEndReportFormValues } from './schema';
import { PeriodEndReportPreview } from './types';

type Recipient = PeriodEndReportFormValues['recipients'][number];

type Props = {
  control: Control<PeriodEndReportFormValues>;
  vehicleId: string;
  baseCurrency: CurrencyCode;
  accountingPeriodId: string;
  periodEndReportPreview: PeriodEndReportPreview;
};

const RecipientList: FC<Props> = ({
  control,
  vehicleId,
  baseCurrency,
  accountingPeriodId,
  periodEndReportPreview,
}) => {
  const { downloadPreviewPdf, isDownloadingPreviewPdf } =
    usePeriodEndReportPreviewPdf();
  const { downloadExcel, isDownloadingExcel } = useFinancialStatementsExcel();

  const { update, fields } = useFieldArray({
    control,
    name: 'recipients',
    keyName: 'fieldId',
  });
  const recipients = fields.map((field) => ({
    id: field.investorId,
    ...field,
  }));

  const toggleEmailNotification = (investorId: string) => {
    const index = recipients.findIndex((r) => r.investorId === investorId);
    if (index === -1) return;
    const recipient = recipients[index];
    update(index, { ...recipient, notifyViaEmail: !recipient.notifyViaEmail });
  };

  const columns: Array<TableColumn<Recipient & { id: string }>> = [
    {
      label: 'Investor',
      key: 'investorId',
      render: ({ investorName }) => (
        <Row centered spacing="xs">
          <Avatar color="random" size="md" text={investorName} />
          <Typography variant="h6">{investorName}</Typography>
        </Row>
      ),
    },
    {
      label: 'Total commitment',
      key: 'totalCommitment',
      render: ({ totalCommitment }) => (
        <Typography variant="h6">
          {numberToCurrencyString(totalCommitment, {
            currency: baseCurrency,
          })}
        </Typography>
      ),
    },
    {
      label: 'Paid-in capital',
      key: 'paidInCapital',
      render: ({ paidInCapital }) => (
        <Typography variant="h6">
          {numberToCurrencyString(paidInCapital, {
            currency: baseCurrency,
          })}
        </Typography>
      ),
    },
    {
      label: 'Email notification',
      key: 'notifyViaEmail',
      render: ({ investorId, notifyViaEmail }) => (
        <Switch
          edge="start"
          checked={Boolean(notifyViaEmail)}
          onChange={() => {
            toggleEmailNotification(investorId);
          }}
        />
      ),
    },
  ];

  return (
    <Column spacing="sm">
      <Typography variant="h5" color="grey.900">
        Recipient list
      </Typography>
      <Table
        columns={columns}
        rows={recipients}
        emptyMessage="No investors."
        rowHasPendingAction={({ investorId }) =>
          isDownloadingPreviewPdf(investorId) || isDownloadingExcel(investorId)
        }
        actions={[
          {
            icon: SaveAlt,
            label: 'Download preview PDF',
            disabled: ({ investorId }) => isDownloadingPreviewPdf(investorId),
            onClick: ({ investorId }) => {
              downloadPreviewPdf({
                vehicleId,
                investorId,
                accountingPeriodId,
                ...periodEndReportPreview,
              });
            },
          },
          {
            icon: ViewListOutlined,
            label: 'Download financial statements',
            disabled: ({ investorId }) => isDownloadingExcel(investorId),
            onClick: ({ investorId }) => {
              downloadExcel({
                vehicleId,
                investorId,
                accountingPeriodId,
              });
            },
          },
          {
            icon: EmailOutlined,
            label: ({ investorId }) => {
              const recipient = recipients.find(
                (r) => r.investorId === investorId,
              );
              return recipient?.notifyViaEmail
                ? 'Exclude from email notification'
                : 'Include in email notification';
            },
            onClick: ({ investorId }) => {
              toggleEmailNotification(investorId);
            },
          },
        ]}
      />
    </Column>
  );
};

export default RecipientList;
